import Cart from "@bevco/MobiusOverrides/Icons/Cart";
import customGetSnapShotData from "@bevco/Store/Handlers/MyAccount/GetSnapshotData";
import { colorPalette, colorStrings } from "@bevco/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Box from "@insite/mobius/Icons/Box";
import File from "@insite/mobius/Icons/File";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { safeColor } from "@insite/mobius/utilities";

import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    destination = "destination",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.destination]?: LinkFieldValue;
    };
}

const mapDispatchToProps = {
    customGetSnapShotData,
};

export type Props = ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface SnapshotStyles {
    title?: TypographyProps;
    container?: GridContainerProps;
    snapshotItem?: GridItemProps;
    snapshotItemWrapper?: InjectableCss;
    leftContent?: InjectableCss;
    itemLabel?: TypographyProps;
    itemValue?: TypographyProps;
    icon?: IconProps;
}

export const snapshotStyles: SnapshotStyles = {
    title: {
        variant: "h2",
        css: css`
            margin-bottom: 1rem;
        `,
    },

    container: {
        gap: 24,
    },

    snapshotItem: {
        width: [12, 12, 4, 4, 4],
    },

    snapshotItemWrapper: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            border: 1px solid ${getColor(colorStrings.commonGlobalGray04)};
            position: relative;
            padding: 32px 32px 26px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            max-width: unset;
            height: 100%;
            width: 100%;

            &::after {
                content: "";
                position: absolute;
                bottom: -1px;
                left: -1px;
                width: calc(100% + 2px);
                height: 6px;
                background-color: ${getColor(colorPalette.primary?.main)};
                transition: 0.2s ease-in-out background-color;
            }
        `,
    },

    leftContent: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },

    itemLabel: {
        size: 14,
        weight: "bold",
    },

    itemValue: {
        size: 32,
        weight: "bold",
        color: colorPalette.primary?.main,
    },

    icon: {
        size: 44,
        color: colorPalette.primary?.main,
    },
};

export const styles = snapshotStyles;

export const SnapshotWidget = ({ customGetSnapShotData, fields }: Props) => {
    const [snapshotInfo, setSnapshotInfo] = useState({}) as any;
    const { url } = fields.destination ? useGetLink(fields.destination) : { url: "" };

    useEffect(() => {
        customGetSnapShotData({
            onSuccess: result => {},
            onComplete: result => {
                if (result.apiResult) {
                    setSnapshotInfo(result.apiResult.OrdersInformation);
                }
            },
        });
    }, []);

    if (!snapshotInfo) {
        return;
    }

    const WrapperElement = ({ children }: any) => {
        if (url) {
            const withHoverStyle = {
                ...styles.snapshotItemWrapper,
                css: css`
                    ${styles.snapshotItemWrapper?.css}
                    transition: 0.2s ease-in-out box-shadow;

                    &:hover {
                        box-shadow: 0 5px 20px 0
                            ${safeColor(getColor(colorStrings.commonGlobalBlack)).rgb().alpha(0.2).string()};

                        &::after {
                            background-color: ${getColor(colorStrings.commonGlobalMaroon)};
                        }
                    }
                `,
            };

            return (
                <Clickable {...withHoverStyle} href={url}>
                    {children}
                </Clickable>
            );
        }
        return <StyledWrapper {...styles.snapshotItemWrapper}>{children}</StyledWrapper>;
    };

    return (
        <>
            <Typography {...styles.title}>{fields.title}</Typography>

            <GridContainer {...styles.container}>
                <GridItem {...styles.snapshotItem}>
                    <StyledWrapper {...styles.snapshotItemWrapper}>
                        <StyledWrapper {...styles.leftContent}>
                            <Typography {...styles.itemLabel}>{translate("Open Orders")}</Typography>
                            <Typography {...styles.itemValue}>{snapshotInfo.OpenOrders}</Typography>
                        </StyledWrapper>
                        <StyledWrapper>
                            <Icon {...styles.icon} src={Box} />
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.snapshotItem}>
                    <WrapperElement>
                        <StyledWrapper {...styles.leftContent}>
                            <Typography {...styles.itemLabel}>{translate("Purchases (YTD)")}</Typography>
                            <Typography {...styles.itemValue}>${snapshotInfo.Purchases}</Typography>
                        </StyledWrapper>
                        <StyledWrapper>
                            <Icon {...styles.icon} src={Cart} />
                        </StyledWrapper>
                    </WrapperElement>
                </GridItem>
                <GridItem {...styles.snapshotItem}>
                    <WrapperElement>
                        <StyledWrapper {...styles.leftContent}>
                            <Typography {...styles.itemLabel}>{translate("Orders Placed (YTD)")}</Typography>
                            <Typography {...styles.itemValue}>{snapshotInfo.OrdersPlaced}</Typography>
                        </StyledWrapper>
                        <StyledWrapper>
                            <Icon {...styles.icon} src={File} />
                        </StyledWrapper>
                    </WrapperElement>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(SnapshotWidget as any) as any,
    definition: {
        displayName: "Snapshot",
        group: "Verndale Box" as any,
        icon: "puzzle-piece",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Snapshot",
                fieldType: "Translatable",
                sortOrder: 1,
                isRequired: true,
            },
            {
                name: fields.destination,
                displayName: "Destination",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                sortOrder: 2,
                isRequired: false,
            },
        ],
    },
};

export default widgetModule;
